import { useEffect, useMemo, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import { ensureRecentAdminReauth } from '@/lib/adminSecurity';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, Circle, Download, Loader2, Lock, RefreshCcw, ShieldCheck, Unlock } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

const formatQty = (value) => Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const SeverityBadge = ({ value }) => {
  const key = String(value || 'ok').toLowerCase();
  const styles = {
    ok: 'bg-[#E8F5EC] text-[#2E7D32] border border-[#CFE8D5]',
    warning: 'bg-[#FFF4E5] text-[#9A6700] border border-[#F1D39A]',
    blocking: 'bg-[#FDECEC] text-[#B42318] border border-[#F4C7C3]',
  };
  return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold uppercase ${styles[key] || styles.ok}`}>{key}</span>;
};

const DetailTable = ({ title, rows, columns, onDownload, emptyText = 'No rows available' }) => (
  <Card className="bg-white border-[#E5E5E5]">
    <CardHeader className="flex flex-row items-center justify-between gap-3">
      <CardTitle className="text-lg text-[#2D2420]">{title}</CardTitle>
      {onDownload ? (
        <Button variant="outline" size="sm" onClick={onDownload}>
          <Download className="w-4 h-4 mr-2" /> Download Excel
        </Button>
      ) : null}
    </CardHeader>
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
            <tr>
              {columns.map((column) => (
                <th key={column.key} className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E5]">
            {rows?.length ? rows.map((row, index) => (
              <tr key={row.id || row.code || row.customer_id || row.vendor_id || row.staff_id || row.raw_item_id || row.variant_id || index}>
                {columns.map((column) => (
                  <td key={column.key} className="py-3 px-4 text-[#2D2420]">
                    {column.render ? column.render(row[column.key], row) : row[column.key] ?? '-'}
                  </td>
                ))}
              </tr>
            )) : (
              <tr><td colSpan={columns.length} className="py-8 text-center text-[#A8A8A8]">{emptyText}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

const emptyCorrection = {
  correction_type: 'finance',
  target_type: 'cash',
  account_code: '',
  amount: '',
  qty: '',
  value: '',
  customer_id: '',
  vendor_id: '',
  warehouse_id: '',
  raw_item_id: '',
  packing_material_item_id: '',
  variant_id: '',
  staff_id: '',
  leave_type: 'casual',
  note: '',
};

const getTimelineStepStatus = ({ done = false, blocked = false, current = false }) => {
  if (done) return 'done';
  if (blocked) return 'blocked';
  if (current) return 'current';
  return 'pending';
};

const TimelineStep = ({ title, description, status, meta }) => {
  const palette = {
    done: {
      dot: 'bg-[#E8F5EC] text-[#2E7D32] border-[#CFE8D5]',
      title: 'text-[#2E7D32]',
      line: 'bg-[#CFE8D5]',
      badge: 'bg-[#E8F5EC] text-[#2E7D32] border-[#CFE8D5]',
    },
    current: {
      dot: 'bg-[#FFF4E5] text-[#9A6700] border-[#F1D39A]',
      title: 'text-[#9A6700]',
      line: 'bg-[#F1D39A]',
      badge: 'bg-[#FFF4E5] text-[#9A6700] border-[#F1D39A]',
    },
    blocked: {
      dot: 'bg-[#FDECEC] text-[#B42318] border-[#F4C7C3]',
      title: 'text-[#B42318]',
      line: 'bg-[#F4C7C3]',
      badge: 'bg-[#FDECEC] text-[#B42318] border-[#F4C7C3]',
    },
    pending: {
      dot: 'bg-white text-[#A8A8A8] border-[#D6D3D1]',
      title: 'text-[#2D2420]',
      line: 'bg-[#E7E5E4]',
      badge: 'bg-white text-[#777] border-[#E5E5E5]',
    },
  };
  const styles = palette[status] || palette.pending;
  const label = status === 'done' ? 'Done' : status === 'current' ? 'Current' : status === 'blocked' ? 'Blocked' : 'Pending';
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className={`flex h-8 w-8 items-center justify-center rounded-full border ${styles.dot}`}>
          {status === 'done' ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-3.5 w-3.5" />}
        </div>
        <div className={`timeline-line mt-2 h-full min-h-8 w-0.5 ${styles.line}`} />
      </div>
      <div className="flex-1 pb-6">
        <div className="flex flex-wrap items-center gap-2">
          <p className={`font-semibold ${styles.title}`}>{title}</p>
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${styles.badge}`}>{label}</span>
        </div>
        <p className="mt-1 text-sm text-[#777]">{description}</p>
        {meta ? <p className="mt-2 text-xs text-[#5C4033]">{meta}</p> : null}
      </div>
    </div>
  );
};

export default function FinancialYearClosePage() {
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedFy, setSelectedFy] = useState('');
  const [closure, setClosure] = useState(null);
  const [control, setControl] = useState(null);
  const [leavePolicies, setLeavePolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [posting, setPosting] = useState(false);
  const [freezing, setFreezing] = useState(false);
  const [releasingFreeze, setReleasingFreeze] = useState(false);
  const [markingBackup, setMarkingBackup] = useState(false);
  const [downloadingPack, setDownloadingPack] = useState(false);
  const [savingPolicies, setSavingPolicies] = useState(false);
  const [savingLeaveSeeds, setSavingLeaveSeeds] = useState(false);
  const [savingInventorySeeds, setSavingInventorySeeds] = useState(false);
  const [creatingCorrection, setCreatingCorrection] = useState(false);
  const [correction, setCorrection] = useState(emptyCorrection);
  const [leaveSeedRows, setLeaveSeedRows] = useState([]);
  const [inventorySeedRows, setInventorySeedRows] = useState([]);
  const [signoffItems, setSignoffItems] = useState([]);
  const [savingSignoff, setSavingSignoff] = useState(false);
  const [confirmationNotes, setConfirmationNotes] = useState('');

  const loadBase = async () => {
    setLoading(true);
    try {
      const [configRes, historyRes, policyRes] = await Promise.all([
        adminApi.getFinancialYearConfig(),
        adminApi.getFinancialYearClosures(),
        adminApi.getFinancialYearLeavePolicies(),
      ]);
      const configData = configRes.data || {};
      setConfig(configData);
      setControl(configData.control || null);
      setHistory(historyRes.data || []);
      setLeavePolicies(policyRes.data || []);
      setSelectedFy((current) => current || configData.closable_fy || configData.current_fy || '');
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Failed to load financial year module');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBase();
  }, []);

  useEffect(() => {
    setLeaveSeedRows((closure?.detailed_rows?.leave_rows || []).map((row) => ({
      staff_id: row.staff_id,
      staff_name: row.staff_name,
      leave_type: row.leave_type,
      carry_forward_rule: row.carry_forward_rule,
      source_days: row.source_days ?? 0,
      opening_days: row.opening_days ?? 0,
      seeded: Boolean(row.seeded),
      unseeded: Boolean(row.unseeded),
      seed_note: row.seed_note || '',
    })));
  }, [closure]);

  useEffect(() => {
    const rows = [
      ...(closure?.detailed_rows?.raw_stock_rows || []),
      ...(closure?.detailed_rows?.packing_material_rows || []),
      ...(closure?.detailed_rows?.finished_stock_rows || []),
    ];
    setInventorySeedRows(rows.map((row) => ({
      line_type: row.line_type,
      warehouse_id: row.warehouse_id,
      raw_item_id: row.raw_item_id,
      packing_material_item_id: row.packing_material_item_id,
      variant_id: row.variant_id,
      stock_ref_no: row.stock_ref_no,
      display_name: row.raw_item_name || row.packing_material_item_name || row.variant_name || '-',
      opening_qty: row.opening_qty ?? 0,
      opening_value: row.opening_value ?? 0,
      missing_value: Boolean(row.missing_value),
      seeded: Boolean(row.seeded),
      seed_note: row.seed_note || '',
    })));
  }, [closure]);

  useEffect(() => {
    setSignoffItems((closure?.signoff_items || []).map((item) => ({
      code: item.code,
      label: item.label,
      required: Boolean(item.required),
      acknowledged: Boolean(item.acknowledged),
      note: item.note || '',
      acknowledged_at: item.acknowledged_at || null,
      acknowledged_by: item.acknowledged_by || null,
    })));
    setConfirmationNotes(closure?.notes || '');
  }, [closure]);

  const openingPreview = closure?.opening_preview || null;
  const detailedRows = closure?.detailed_rows || {};
  const postChecks = closure?.post_checks || [];
  const healthChecks = closure?.health_checks || [];
  const summary = closure?.summary || {};
  const blockingIssues = healthChecks.filter((item) => item?.blocking);
  const missingSignoffs = signoffItems.filter((item) => item.required && !item.acknowledged);
  const unseededLeaveCount = leaveSeedRows.filter((row) => row.unseeded).length;
  const missingInventoryValueCount = inventorySeedRows.filter((row) => row.missing_value).length;
  const allSignoffDone = signoffItems.length > 0 && missingSignoffs.length === 0;
  const openingsVerified = closure?.status === 'openings_generated' && !postChecks.some((item) => item?.blocking);

  const timelineSteps = useMemo(() => {
    const freezeActive = ['freeze_active', 'closure_confirmed', 'openings_generated'].includes(String(control?.state || ''));
    const backupDone = Boolean(control?.backup_completed);
    const previewDone = Boolean(closure?.id);
    const blockersResolved = previewDone && blockingIssues.length === 0;
    const leaveReviewed = previewDone && unseededLeaveCount === 0;
    const inventoryReviewed = previewDone && missingInventoryValueCount === 0;
    const closeConfirmed = ['confirmed', 'openings_generated'].includes(String(closure?.status || ''));
    const openingsPosted = String(closure?.status || '') === 'openings_generated';

    const steps = [
      {
        key: 'freeze',
        title: 'Enable Transaction Freeze',
        description: 'Freeze all FY-sensitive transactions before starting the year-end process.',
        status: getTimelineStepStatus({
          done: freezeActive,
          blocked: false,
          current: !freezeActive,
        }),
        meta: freezeActive ? `Freeze started ${formatDateTime(control?.freeze_started_at)}` : 'Use Enable Freeze before running FY preview.',
      },
      {
        key: 'backup',
        title: 'Take and Verify Backup',
        description: 'Take the final DB backup and mark it complete only after verifying it.',
        status: getTimelineStepStatus({
          done: backupDone,
          blocked: !freezeActive,
          current: freezeActive && !backupDone,
        }),
        meta: backupDone ? 'Backup marked complete for this close attempt.' : 'Backup must be completed before close confirmation.',
      },
      {
        key: 'preview',
        title: 'Run FY Preview and Download Closing Pack',
        description: 'Generate the closing snapshot, review the tables, and download the closing pack and Excel files.',
        status: getTimelineStepStatus({
          done: previewDone,
          blocked: !freezeActive || !backupDone,
          current: freezeActive && backupDone && !previewDone,
        }),
        meta: previewDone ? `Preview ready for FY ${closure?.fy_key}` : 'Run FY Health Check after freeze and backup are complete.',
      },
      {
        key: 'reviews',
        title: 'Resolve Blockers and Review Leave / Inventory',
        description: 'Clear blocking checks, review leave openings, and review raw, packing, and finished-goods opening rows.',
        status: getTimelineStepStatus({
          done: blockersResolved && leaveReviewed && inventoryReviewed,
          blocked: previewDone && (!blockersResolved || !leaveReviewed || !inventoryReviewed),
          current: previewDone && !(blockersResolved && leaveReviewed && inventoryReviewed),
        }),
        meta: `Blocking checks: ${blockingIssues.length} • Unseeded leave rows: ${unseededLeaveCount} • Missing valuations: ${missingInventoryValueCount}`,
      },
      {
        key: 'signoff',
        title: 'Complete Final Sign-Off',
        description: 'Acknowledge the final checklist so the FY close is intentionally approved.',
        status: getTimelineStepStatus({
          done: allSignoffDone,
          blocked: previewDone && (!blockersResolved || !leaveReviewed || !inventoryReviewed),
          current: previewDone && blockersResolved && leaveReviewed && inventoryReviewed && !allSignoffDone,
        }),
        meta: allSignoffDone ? 'All required sign-off items acknowledged.' : `${missingSignoffs.length} required sign-off items pending.`,
      },
      {
        key: 'confirm',
        title: 'Confirm FY Close',
        description: 'Freeze the closing snapshot so it becomes the approved basis for the new FY openings.',
        status: getTimelineStepStatus({
          done: closeConfirmed,
          blocked: previewDone && (!allSignoffDone || !blockersResolved),
          current: previewDone && allSignoffDone && blockersResolved && !closeConfirmed,
        }),
        meta: closeConfirmed ? `Confirmed ${formatDateTime(closure?.confirmed_at)}` : 'Confirmation unlocks opening generation.',
      },
      {
        key: 'openings',
        title: 'Post and Verify Opening Balances',
        description: 'Generate opening entries, verify post-checks, and only then allow transactions again.',
        status: getTimelineStepStatus({
          done: openingsVerified,
          blocked: !closeConfirmed || (openingsPosted && !openingsVerified),
          current: closeConfirmed && !openingsVerified,
        }),
        meta: openingsPosted
          ? openingsVerified
            ? `Openings posted ${formatDateTime(closure?.opening_generated_at)} and post-checks are clear.`
            : 'Openings were posted, but post-checks still need attention.'
          : 'Post openings after confirming the close.',
      },
    ];
    return steps;
  }, [blockingIssues.length, closure, control, inventorySeedRows, leaveSeedRows, missingInventoryValueCount, missingSignoffs.length, postChecks, signoffItems.length, unseededLeaveCount, allSignoffDone]);

  const availableFyOptions = useMemo(() => {
    const values = new Set();
    if (config?.closable_fy) values.add(config.closable_fy);
    if (config?.current_fy) values.add(config.current_fy);
    if (config?.next_fy) values.add(config.next_fy);
    history.forEach((item) => {
      if (item?.fy_key) values.add(item.fy_key);
    });
    return Array.from(values).sort();
  }, [config, history]);

  const handlePreview = async () => {
    if (!selectedFy) {
      toast.error('Select a financial year first');
      return;
    }
    setPreviewing(true);
    try {
      const res = await adminApi.previewFinancialYearClosure({ fy_key: selectedFy });
      setClosure(res.data || null);
      setControl(res.data?.control || control);
      const historyRes = await adminApi.getFinancialYearClosures();
      setHistory(historyRes.data || []);
      toast.success('FY preview generated');
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Failed to generate FY preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    if (!closure?.id) {
      toast.error('Generate preview first');
      return;
    }
    setConfirming(true);
    try {
      const confirmed = await ensureRecentAdminReauth();
      if (!confirmed) return;
      const res = await adminApi.confirmFinancialYearClosure(closure.id, { notes: confirmationNotes });
      setClosure((prev) => ({ ...(prev || {}), ...(res.data || {}) }));
      const controlRes = await adminApi.getFinancialYearControl();
      setControl(controlRes.data || null);
      const historyRes = await adminApi.getFinancialYearClosures();
      setHistory(historyRes.data || []);
      toast.success('FY closure confirmed');
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Failed to confirm FY closure');
    } finally {
      setConfirming(false);
    }
  };

  const handleSaveSignoff = async () => {
    if (!closure?.id) {
      toast.error('Generate FY preview first');
      return;
    }
    setSavingSignoff(true);
    try {
      await adminApi.updateFinancialYearSignoff(
        closure.id,
        signoffItems.map((item) => ({
          code: item.code,
          acknowledged: Boolean(item.acknowledged),
          note: item.note || '',
        }))
      );
      const detailRes = await adminApi.getFinancialYearClosureById(closure.id, { include_lines: true });
      setClosure(detailRes.data || null);
      toast.success('FY sign-off updated');
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Failed to update FY sign-off');
    } finally {
      setSavingSignoff(false);
    }
  };

  const handlePostOpenings = async () => {
    if (!closure?.id) {
      toast.error('Generate preview first');
      return;
    }
    setPosting(true);
    try {
      const confirmed = await ensureRecentAdminReauth();
      if (!confirmed) return;
      const res = await adminApi.postFinancialYearOpenings(closure.id, {});
      setClosure((prev) => ({ ...(prev || {}), ...(res.data?.closure || {}) }));
      const detailRes = await adminApi.getFinancialYearClosureById(closure.id, { include_lines: true });
      setClosure(detailRes.data || null);
      const controlRes = await adminApi.getFinancialYearControl();
      setControl(controlRes.data || null);
      const historyRes = await adminApi.getFinancialYearClosures();
      setHistory(historyRes.data || []);
      toast.success('Opening balances posted');
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Failed to post opening balances');
    } finally {
      setPosting(false);
    }
  };

  const handleSavePolicies = async () => {
    setSavingPolicies(true);
    try {
      await adminApi.updateFinancialYearLeavePolicies(
        leavePolicies.map((item) => ({
          leave_type: item.leave_type,
          carry_forward_rule: item.carry_forward_rule,
          max_carry_days: Number(item.max_carry_days || 0),
        }))
      );
      toast.success('Leave carry-forward rules updated');
      const res = await adminApi.getFinancialYearLeavePolicies();
      setLeavePolicies(res.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save leave policies');
    } finally {
      setSavingPolicies(false);
    }
  };

  const handleSaveLeaveSeeds = async () => {
    if (!closure?.id) {
      toast.error('Generate FY preview first');
      return;
    }
    setSavingLeaveSeeds(true);
    try {
      await adminApi.updateFinancialYearLeaveSeeds(
        closure.id,
        leaveSeedRows.map((row) => ({
          staff_id: row.staff_id,
          leave_type: row.leave_type,
          source_days: row.source_days === '' ? null : Number(row.source_days || 0),
          opening_days: Number(row.opening_days || 0),
          note: row.seed_note || '',
        }))
      );
      const detailRes = await adminApi.getFinancialYearClosureById(closure.id, { include_lines: true });
      setClosure(detailRes.data || null);
      toast.success('Leave opening review saved');
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Failed to save leave opening review');
    } finally {
      setSavingLeaveSeeds(false);
    }
  };

  const handleSaveInventorySeeds = async () => {
    if (!closure?.id) {
      toast.error('Generate FY preview first');
      return;
    }
    setSavingInventorySeeds(true);
    try {
      await adminApi.updateFinancialYearInventorySeeds(
        closure.id,
        inventorySeedRows.map((row) => ({
          line_type: row.line_type,
          warehouse_id: row.warehouse_id,
          raw_item_id: row.raw_item_id,
          packing_material_item_id: row.packing_material_item_id,
          variant_id: row.variant_id,
          stock_ref_no: row.stock_ref_no,
          opening_value: Number(row.opening_value || 0),
          note: row.seed_note || '',
        }))
      );
      const detailRes = await adminApi.getFinancialYearClosureById(closure.id, { include_lines: true });
      setClosure(detailRes.data || null);
      toast.success('Inventory valuation review saved');
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Failed to save inventory valuation review');
    } finally {
      setSavingInventorySeeds(false);
    }
  };

  const handleStartFreeze = async () => {
    setFreezing(true);
    try {
      const res = await adminApi.startFinancialYearFreeze(closure?.id ? { closure_id: closure.id } : {});
      setControl(res.data?.control || null);
      toast.success('FY freeze enabled');
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Failed to enable FY freeze');
    } finally {
      setFreezing(false);
    }
  };

  const handleReleaseFreeze = async () => {
    setReleasingFreeze(true);
    try {
      const res = await adminApi.releaseFinancialYearFreeze();
      setControl(res.data?.control || null);
      toast.success('FY freeze released');
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Failed to release FY freeze');
    } finally {
      setReleasingFreeze(false);
    }
  };

  const handleMarkBackup = async () => {
    setMarkingBackup(true);
    try {
      const res = await adminApi.markFinancialYearBackupComplete();
      setControl(res.data?.control || null);
      toast.success('Backup marked complete');
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Failed to mark backup complete');
    } finally {
      setMarkingBackup(false);
    }
  };

  const handleDownloadSection = async (section) => {
    if (!closure?.id) {
      toast.error('Generate FY preview first');
      return;
    }
    try {
      const res = await adminApi.downloadFinancialYearSectionExcel(closure.id, section);
      downloadBlob(res.data, `${section}.xlsx`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to download Excel');
    }
  };

  const handleDownloadPack = async () => {
    if (!closure?.id) {
      toast.error('Generate FY preview first');
      return;
    }
    setDownloadingPack(true);
    try {
      const res = await adminApi.downloadFinancialYearClosingPack(closure.id);
      downloadBlob(res.data, `fy-closing-pack-${closure.fy_key || 'export'}.xlsx`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to download FY closing pack');
    } finally {
      setDownloadingPack(false);
    }
  };

  const handleCreateCorrection = async () => {
    if (!closure?.id) {
      toast.error('Generate and post openings before adding corrections');
      return;
    }
    setCreatingCorrection(true);
    try {
      const confirmed = await ensureRecentAdminReauth();
      if (!confirmed) return;
      await adminApi.createFinancialYearCorrection(closure.id, {
        ...correction,
        amount: correction.amount === '' ? undefined : Number(correction.amount),
        qty: correction.qty === '' ? undefined : Number(correction.qty),
        value: correction.value === '' ? undefined : Number(correction.value),
      });
      toast.success('Opening correction recorded');
      setCorrection(emptyCorrection);
      const detailRes = await adminApi.getFinancialYearClosureById(closure.id, { include_lines: true });
      setClosure(detailRes.data || null);
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Failed to record correction');
    } finally {
      setCreatingCorrection(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="financial-year-close-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Financial Year Close</h1>
          <p className="text-[#A8A8A8] mt-1">Preview, confirm, and generate opening balances for April to March financial years</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={selectedFy} onValueChange={setSelectedFy}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Select FY" />
            </SelectTrigger>
            <SelectContent>
              {availableFyOptions.map((item) => (
                <SelectItem key={item} value={item}>{item}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadBase} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
          <Button variant="outline" onClick={handleStartFreeze} disabled={freezing || ['freeze_active', 'closure_confirmed'].includes(String(control?.state || ''))}>
            {freezing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
            Enable Freeze
          </Button>
          <Button variant="outline" onClick={handleMarkBackup} disabled={markingBackup || Boolean(control?.backup_completed)}>
            {markingBackup ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            Mark Backup Complete
          </Button>
          <Button variant="outline" onClick={handleReleaseFreeze} disabled={releasingFreeze || !['freeze_active', 'closure_confirmed'].includes(String(control?.state || ''))}>
            {releasingFreeze ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unlock className="w-4 h-4 mr-2" />}
            Release Freeze
          </Button>
          <Button className="bg-[#C97A5E] hover:bg-[#B06850]" onClick={handlePreview} disabled={previewing || loading}>
            {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Run FY Health Check
          </Button>
          <Button onClick={handleConfirm} disabled={!closure?.id || confirming || !!blockingIssues.length || !!missingSignoffs.length || closure?.status === 'confirmed' || closure?.status === 'openings_generated'}>
            {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Confirm Close
          </Button>
          <Button variant="secondary" onClick={handlePostOpenings} disabled={!closure?.id || posting || closure?.status !== 'confirmed'}>
            {posting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Confirm and Post Openings
          </Button>
          <Button variant="outline" onClick={handleDownloadPack} disabled={!closure?.id || downloadingPack}>
            {downloadingPack ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download FY Closing Pack
          </Button>
        </div>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="text-sm text-[#A8A8A8]">Freeze State</p>
            <p className="text-lg font-semibold text-[#2D2420]">{control?.state || 'idle'}</p>
          </div>
          <div>
            <p className="text-sm text-[#A8A8A8]">Backup Status</p>
            <p className="text-lg font-semibold text-[#2D2420]">{control?.backup_completed ? 'Completed' : 'Pending'}</p>
          </div>
          <div>
            <p className="text-sm text-[#A8A8A8]">Freeze Started</p>
            <p className="text-lg font-semibold text-[#2D2420]">{formatDateTime(control?.freeze_started_at)}</p>
          </div>
          <div>
            <p className="text-sm text-[#A8A8A8]">Active Closure</p>
            <p className="text-lg font-semibold text-[#2D2420]">{control?.active_closure_id || '-'}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">FY Close Timeline Checklist</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-[#E5E5E5] bg-[#FAFAF9] px-4 py-3 mb-5">
            <p className="text-sm text-[#5C4033]">
              Follow this sequence exactly. The current stage, completed stages, and blockers are shown here so no step gets missed during year-end.
            </p>
          </div>
          <div>
            {timelineSteps.map((step, index) => (
              <div key={step.key} className={index === timelineSteps.length - 1 ? '[&_.timeline-line]:hidden' : ''}>
                <TimelineStep title={step.title} description={step.description} status={step.status} meta={step.meta} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Receivables</p>
            <p className="text-2xl font-bold text-[#2D2420]">{formatCurrency(summary.receivables_total)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Payables</p>
            <p className="text-2xl font-bold text-[#2D2420]">{formatCurrency(summary.payables_total)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Cash + Bank</p>
            <p className="text-2xl font-bold text-[#2D2420]">{formatCurrency((summary.cash_balance || 0) + (summary.bank_balance || 0))}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Staff Transit</p>
            <p className="text-2xl font-bold text-[#C97A5E]">{formatCurrency(summary.staff_transit_balance)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Blocking Checks</p>
            <p className="text-2xl font-bold text-[#B42318]">{summary.blocking_issue_count || 0}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Current Preview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!closure ? (
            <p className="text-sm text-[#777]">Run the FY health check to generate a draft closure snapshot.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant="outline">{closure.fy_key}</Badge>
                <SeverityBadge value={blockingIssues.length ? 'blocking' : 'ok'} />
                <Badge variant="outline">Status: {closure.status}</Badge>
                <Badge variant="outline">Snapshot: {formatDateTime(closure.snapshot_at)}</Badge>
                {closure.confirmed_at ? <Badge variant="outline">Confirmed: {formatDateTime(closure.confirmed_at)}</Badge> : null}
                {closure.opening_generated_at ? <Badge variant="outline">Openings: {formatDateTime(closure.opening_generated_at)}</Badge> : null}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-lg border border-[#E5E5E5] p-4">
                  <p className="text-sm text-[#A8A8A8]">Raw Stock</p>
                  <p className="font-semibold text-[#2D2420]">{formatQty(summary.raw_stock_qty_total)} kg</p>
                  <p className="text-sm text-[#777]">{formatCurrency(summary.raw_stock_value_total)}</p>
                </div>
                <div className="rounded-lg border border-[#E5E5E5] p-4">
                  <p className="text-sm text-[#A8A8A8]">Packing Materials</p>
                  <p className="font-semibold text-[#2D2420]">{formatQty(summary.packing_material_qty_total)}</p>
                  <p className="text-sm text-[#777]">{formatCurrency(summary.packing_material_value_total)}</p>
                </div>
                <div className="rounded-lg border border-[#E5E5E5] p-4">
                  <p className="text-sm text-[#A8A8A8]">Finished Goods</p>
                  <p className="font-semibold text-[#2D2420]">{formatQty(summary.finished_goods_qty_total)}</p>
                  <p className="text-sm text-[#777]">{formatCurrency(summary.finished_goods_value_total)}</p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-lg text-[#2D2420]">FY Health Checks</CardTitle>
          <Button variant="outline" size="sm" onClick={() => handleDownloadSection('health-checks')} disabled={!closure?.id}>
            <Download className="w-4 h-4 mr-2" /> Download Excel
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Check</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Severity</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Count</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {healthChecks.map((item) => (
                  <tr key={item.code}>
                    <td className="py-3 px-4 text-[#2D2420]">
                      <p className="font-medium">{item.message}</p>
                      <p className="text-xs text-[#777]">{item.code}</p>
                    </td>
                    <td className="py-3 px-4"><SeverityBadge value={item.severity} /></td>
                    <td className="py-3 px-4 text-[#2D2420]">{item.count || 0}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{formatCurrency(item.amount || 0)}</td>
                  </tr>
                ))}
                {!healthChecks.length ? (
                  <tr><td colSpan={4} className="py-8 text-center text-[#A8A8A8]">No FY checks yet. Generate preview first.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle className="text-lg text-[#2D2420]">Inventory Valuation Review</CardTitle>
            <p className="text-sm text-[#777] mt-1">Review raw, packing-material, and finished-goods opening rows. Rows with missing valuation must be completed before posting openings.</p>
          </div>
          <Button variant="outline" onClick={handleSaveInventorySeeds} disabled={savingInventorySeeds || !closure?.id}>
            {savingInventorySeeds ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Valuation Review
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Type</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Item</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Warehouse</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Stock Ref</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Qty</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Opening Value</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {inventorySeedRows.length ? inventorySeedRows.map((row, index) => (
                  <tr key={`${row.line_type}-${row.warehouse_id}-${row.raw_item_id || row.packing_material_item_id || row.variant_id || ''}-${row.stock_ref_no || index}`}>
                    <td className="py-3 px-4 text-[#2D2420]">{row.line_type === 'raw_stock' ? 'Raw Stock' : row.line_type === 'packing_material_stock' ? 'Packing Material' : 'Finished Stock'}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{row.display_name}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{row.warehouse_id || '-'}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{row.stock_ref_no || '-'}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{formatQty(row.opening_qty)}</td>
                    <td className="py-3 px-4">
                      <Input
                        type="number"
                        step="0.01"
                        value={row.opening_value}
                        onChange={(e) => setInventorySeedRows((prev) => prev.map((item, rowIndex) => rowIndex === index ? { ...item, opening_value: e.target.value, missing_value: Number(e.target.value || 0) === 0 } : item))}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className={row.missing_value ? 'border-[#F4C7C3] text-[#B42318]' : row.seeded ? 'border-[#CFE8D5] text-[#2E7D32]' : 'border-[#E5E5E5] text-[#777]'}>
                        {row.missing_value ? 'Missing Value' : row.seeded ? 'Reviewed' : 'System Value'}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Input
                        value={row.seed_note}
                        onChange={(e) => setInventorySeedRows((prev) => prev.map((item, rowIndex) => rowIndex === index ? { ...item, seed_note: e.target.value } : item))}
                        placeholder="Optional note"
                      />
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={8} className="py-8 text-center text-[#A8A8A8]">Inventory valuation rows will appear after FY preview generation.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {postChecks.length ? (
        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="text-lg text-[#2D2420]">Opening Posting Checks</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Check</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Severity</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Count</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {postChecks.map((item) => (
                    <tr key={item.code}>
                      <td className="py-3 px-4 text-[#2D2420]">{item.message}</td>
                      <td className="py-3 px-4"><SeverityBadge value={item.severity} /></td>
                      <td className="py-3 px-4 text-[#2D2420]">{item.count || 0}</td>
                      <td className="py-3 px-4 text-[#2D2420]">{formatCurrency(item.amount || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle className="text-lg text-[#2D2420]">Final Sign-Off Checklist</CardTitle>
            <p className="text-sm text-[#777] mt-1">Complete this before confirming the FY close. Confirmation is blocked until all required items are acknowledged.</p>
          </div>
          <Button variant="outline" onClick={handleSaveSignoff} disabled={savingSignoff || !closure?.id || closure?.status === 'confirmed' || closure?.status === 'openings_generated'}>
            {savingSignoff ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Sign-Off
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {missingSignoffs.length ? (
            <div className="rounded-lg border border-[#F4C7C3] bg-[#FDECEC] px-4 py-3 text-sm text-[#B42318]">
              {missingSignoffs.length} required sign-off item{missingSignoffs.length > 1 ? 's are' : ' is'} still pending.
            </div>
          ) : signoffItems.length ? (
            <div className="rounded-lg border border-[#CFE8D5] bg-[#E8F5EC] px-4 py-3 text-sm text-[#2E7D32]">
              All required sign-off items are acknowledged.
            </div>
          ) : null}
          <div className="space-y-3">
            {signoffItems.map((item, index) => (
              <div key={item.code} className="rounded-lg border border-[#E5E5E5] p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={item.acknowledged}
                    onCheckedChange={(checked) => setSignoffItems((prev) => prev.map((row, rowIndex) => rowIndex === index ? { ...row, acknowledged: Boolean(checked) } : row))}
                    disabled={closure?.status === 'confirmed' || closure?.status === 'openings_generated'}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-[#2D2420]">{item.label}</p>
                      {item.required ? <Badge variant="outline">Required</Badge> : null}
                    </div>
                    {item.acknowledged_at ? (
                      <p className="text-xs text-[#777] mt-1">
                        Acknowledged {formatDateTime(item.acknowledged_at)}{item.acknowledged_by?.name ? ` by ${item.acknowledged_by.name}` : ''}
                      </p>
                    ) : null}
                  </div>
                </div>
                <Input
                  value={item.note}
                  onChange={(e) => setSignoffItems((prev) => prev.map((row, rowIndex) => rowIndex === index ? { ...row, note: e.target.value } : row))}
                  placeholder="Optional sign-off note"
                  disabled={closure?.status === 'confirmed' || closure?.status === 'openings_generated'}
                />
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-[#2D2420]">Confirmation Notes</p>
            <Textarea
              value={confirmationNotes}
              onChange={(e) => setConfirmationNotes(e.target.value)}
              placeholder="Optional note to store on the confirmed FY close"
              disabled={closure?.status === 'confirmed' || closure?.status === 'openings_generated'}
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg text-[#2D2420]">Leave Carry-Forward Rules</CardTitle>
            <Button variant="outline" onClick={handleSavePolicies} disabled={savingPolicies}>
              {savingPolicies ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Rules
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {leavePolicies.map((item, index) => (
              <div key={item.leave_type} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center rounded-lg border border-[#E5E5E5] p-3">
                <div>
                  <p className="font-medium text-[#2D2420] capitalize">{item.leave_type}</p>
                  <p className="text-xs text-[#777]">Applied when generating new FY leave openings</p>
                </div>
                <Select
                  value={item.carry_forward_rule}
                  onValueChange={(value) =>
                    setLeavePolicies((prev) =>
                      prev.map((row, rowIndex) => (rowIndex === index ? { ...row, carry_forward_rule: value } : row))
                    )
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Rule" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Carry Forward</SelectItem>
                    <SelectItem value="full">Full Carry Forward</SelectItem>
                    <SelectItem value="partial">Partial Carry Forward</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={item.max_carry_days}
                  onChange={(e) =>
                    setLeavePolicies((prev) =>
                      prev.map((row, rowIndex) => (rowIndex === index ? { ...row, max_carry_days: e.target.value } : row))
                    )
                  }
                  placeholder="Max carry days"
                />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="text-lg text-[#2D2420]">Opening Balance Preview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {!openingPreview ? (
              <p className="text-sm text-[#777]">Opening preview will appear after FY preview generation.</p>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-lg border border-[#E5E5E5] p-4">
                    <p className="text-sm text-[#A8A8A8]">Customer Openings</p>
                    <p className="font-semibold text-[#2D2420]">{openingPreview.finance?.receivable_rows?.length || 0} rows</p>
                    <p className="text-sm text-[#777]">{formatCurrency(openingPreview.finance?.receivables_total || 0)}</p>
                  </div>
                  <div className="rounded-lg border border-[#E5E5E5] p-4">
                    <p className="text-sm text-[#A8A8A8]">Vendor Openings</p>
                    <p className="font-semibold text-[#2D2420]">{openingPreview.finance?.payable_rows?.length || 0} rows</p>
                    <p className="text-sm text-[#777]">{formatCurrency(openingPreview.finance?.payables_total || 0)}</p>
                  </div>
                  <div className="rounded-lg border border-[#E5E5E5] p-4">
                    <p className="text-sm text-[#A8A8A8]">Inventory Openings</p>
                    <p className="font-semibold text-[#2D2420]">
                      {(openingPreview.inventory?.raw_stock_rows?.length || 0) + (openingPreview.inventory?.packing_material_rows?.length || 0) + (openingPreview.inventory?.finished_stock_rows?.length || 0)} rows
                    </p>
                    <p className="text-sm text-[#777]">
                      Raw {openingPreview.inventory?.raw_stock_rows?.length || 0} • Packing {openingPreview.inventory?.packing_material_rows?.length || 0} • Finished {openingPreview.inventory?.finished_stock_rows?.length || 0}
                    </p>
                  </div>
                  <div className="rounded-lg border border-[#E5E5E5] p-4">
                    <p className="text-sm text-[#A8A8A8]">Leave Openings</p>
                    <p className="font-semibold text-[#2D2420]">{openingPreview.leave?.rows?.length || 0} rows</p>
                    <p className="text-sm text-[#777]">{closure?.to_fy || '-'}</p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle className="text-lg text-[#2D2420]">Leave Opening Seed Review</CardTitle>
            <p className="text-sm text-[#777] mt-1">Review or seed leave openings before posting openings. Rows marked unseeded should not be left unattended.</p>
          </div>
          <Button variant="outline" onClick={handleSaveLeaveSeeds} disabled={savingLeaveSeeds || !closure?.id}>
            {savingLeaveSeeds ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Leave Review
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Staff</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Leave Type</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Rule</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Source Days</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Opening Days</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Seed Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {leaveSeedRows.length ? leaveSeedRows.map((row, index) => (
                  <tr key={`${row.staff_id}-${row.leave_type}`}>
                    <td className="py-3 px-4 text-[#2D2420]">{row.staff_name}</td>
                    <td className="py-3 px-4 text-[#2D2420] capitalize">{row.leave_type}</td>
                    <td className="py-3 px-4 text-[#2D2420] capitalize">{row.carry_forward_rule}</td>
                    <td className="py-3 px-4">
                      <Input
                        type="number"
                        step="0.5"
                        value={row.source_days}
                        onChange={(e) => setLeaveSeedRows((prev) => prev.map((item, rowIndex) => rowIndex === index ? { ...item, source_days: e.target.value } : item))}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <Input
                        type="number"
                        step="0.5"
                        value={row.opening_days}
                        onChange={(e) => setLeaveSeedRows((prev) => prev.map((item, rowIndex) => rowIndex === index ? { ...item, opening_days: e.target.value, seeded: true, unseeded: false } : item))}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className={row.unseeded ? 'border-[#F4C7C3] text-[#B42318]' : row.seeded ? 'border-[#CFE8D5] text-[#2E7D32]' : 'border-[#E5E5E5] text-[#777]'}>
                        {row.unseeded ? 'Unseeded' : row.seeded ? 'Seeded' : 'Policy'}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Input
                        value={row.seed_note}
                        onChange={(e) => setLeaveSeedRows((prev) => prev.map((item, rowIndex) => rowIndex === index ? { ...item, seed_note: e.target.value } : item))}
                        placeholder="Optional note"
                      />
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={7} className="py-8 text-center text-[#A8A8A8]">Leave opening rows will appear after FY preview generation.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Opening Corrections</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-[#777]">Use this only after openings are generated. Corrections are additive and do not reopen the confirmed close snapshot.</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Select value={correction.correction_type} onValueChange={(value) => setCorrection((prev) => ({ ...prev, correction_type: value }))}>
              <SelectTrigger><SelectValue placeholder="Correction type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="finance">Finance</SelectItem>
                <SelectItem value="stock">Stock</SelectItem>
                <SelectItem value="staff_transit">Staff Transit</SelectItem>
                <SelectItem value="leave">Leave</SelectItem>
              </SelectContent>
            </Select>
            <Select value={correction.target_type} onValueChange={(value) => setCorrection((prev) => ({ ...prev, target_type: value }))}>
              <SelectTrigger><SelectValue placeholder="Target" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="bank">Bank</SelectItem>
                <SelectItem value="customer_receivable">Customer Receivable</SelectItem>
                <SelectItem value="vendor_payable">Vendor Payable</SelectItem>
                <SelectItem value="raw_stock">Raw Stock</SelectItem>
                <SelectItem value="packing_material_stock">Packing Material Stock</SelectItem>
                <SelectItem value="finished_stock">Finished Stock</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Amount" type="number" value={correction.amount} onChange={(e) => setCorrection((prev) => ({ ...prev, amount: e.target.value }))} />
            <Input placeholder="Qty / Days" type="number" value={correction.qty} onChange={(e) => setCorrection((prev) => ({ ...prev, qty: e.target.value }))} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input placeholder="Value" type="number" value={correction.value} onChange={(e) => setCorrection((prev) => ({ ...prev, value: e.target.value }))} />
            <Input placeholder="Customer ID" value={correction.customer_id} onChange={(e) => setCorrection((prev) => ({ ...prev, customer_id: e.target.value }))} />
            <Input placeholder="Vendor ID / Staff ID" value={correction.vendor_id || correction.staff_id} onChange={(e) => setCorrection((prev) => ({ ...prev, vendor_id: e.target.value, staff_id: e.target.value }))} />
          </div>
          <Textarea placeholder="Correction note" value={correction.note} onChange={(e) => setCorrection((prev) => ({ ...prev, note: e.target.value }))} />
          <div className="flex justify-end">
            <Button onClick={handleCreateCorrection} disabled={creatingCorrection || closure?.status !== 'openings_generated'}>
              {creatingCorrection ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Create Correction
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6">
        <DetailTable
          title="Customer Outstanding Mismatch Details"
          rows={detailedRows.customer_outstanding_mismatch_rows || []}
          onDownload={() => handleDownloadSection('customer-outstanding-mismatches')}
          emptyText="No customer outstanding mismatches for this FY snapshot."
          columns={[
            { key: 'customer_name', label: 'Customer' },
            { key: 'shop_name', label: 'Shop' },
            { key: 'phone', label: 'Phone' },
            { key: 'stored_outstanding_amount', label: 'Stored Outstanding', render: (value) => formatCurrency(value) },
            { key: 'cutoff_outstanding_amount', label: 'Cutoff Outstanding', render: (value) => formatCurrency(value) },
            { key: 'difference_amount', label: 'Difference', render: (value) => formatCurrency(value) },
          ]}
        />
        <DetailTable
          title="Customer Receivables"
          rows={detailedRows.customer_receivable_rows || []}
          onDownload={() => handleDownloadSection('customer-receivables')}
          columns={[
            { key: 'customer_name', label: 'Customer' },
            { key: 'shop_name', label: 'Shop' },
            { key: 'area', label: 'Area' },
            { key: 'amount', label: 'Amount', render: (value) => formatCurrency(value) },
          ]}
        />
        <DetailTable
          title="Vendor Payables"
          rows={detailedRows.vendor_payable_rows || []}
          onDownload={() => handleDownloadSection('vendor-payables')}
          columns={[
            { key: 'vendor_name', label: 'Vendor' },
            { key: 'po_count', label: 'PO Count' },
            { key: 'amount', label: 'Amount', render: (value) => formatCurrency(value) },
          ]}
        />
        <DetailTable
          title="Cash, Bank, and Staff Transit"
          rows={detailedRows.cash_bank_rows || []}
          onDownload={() => handleDownloadSection('cash-bank-transit')}
          columns={[
            { key: 'line_type', label: 'Type' },
            { key: 'account_code', label: 'Account' },
            { key: 'amount', label: 'Amount', render: (value) => formatCurrency(value) },
          ]}
        />
        <DetailTable
          title="Raw Stock Openings"
          rows={detailedRows.raw_stock_rows || []}
          onDownload={() => handleDownloadSection('raw-stock-openings')}
          columns={[
            { key: 'raw_item_name', label: 'Raw Item' },
            { key: 'warehouse_id', label: 'Warehouse ID' },
            { key: 'stock_ref_no', label: 'Stock Ref' },
            { key: 'opening_qty', label: 'Opening Qty', render: (value) => formatQty(value) },
            { key: 'opening_value', label: 'Opening Value', render: (value) => formatCurrency(value) },
          ]}
        />
        <DetailTable
          title="Packing Material Openings"
          rows={detailedRows.packing_material_rows || []}
          onDownload={() => handleDownloadSection('packing-material-openings')}
          columns={[
            { key: 'packing_material_item_name', label: 'Item' },
            { key: 'warehouse_id', label: 'Warehouse ID' },
            { key: 'stock_ref_no', label: 'Stock Ref' },
            { key: 'opening_qty', label: 'Opening Qty', render: (value) => formatQty(value) },
            { key: 'opening_value', label: 'Opening Value', render: (value) => formatCurrency(value) },
          ]}
        />
        <DetailTable
          title="Finished Stock Openings"
          rows={detailedRows.finished_stock_rows || []}
          onDownload={() => handleDownloadSection('finished-stock-openings')}
          columns={[
            { key: 'variant_name', label: 'Variant' },
            { key: 'warehouse_id', label: 'Warehouse ID' },
            { key: 'opening_qty', label: 'Opening Qty', render: (value) => formatQty(value) },
            { key: 'opening_value', label: 'Opening Value', render: (value) => formatCurrency(value) },
          ]}
        />
        <DetailTable
          title="Leave Openings"
          rows={detailedRows.leave_rows || []}
          onDownload={() => handleDownloadSection('leave-openings')}
          columns={[
            { key: 'staff_name', label: 'Staff' },
            { key: 'leave_type', label: 'Leave Type' },
            { key: 'carry_forward_rule', label: 'Rule' },
            { key: 'source_days', label: 'Source Days', render: (value) => formatQty(value) },
            { key: 'opening_days', label: 'Opening Days', render: (value) => formatQty(value) },
            { key: 'remaining_days', label: 'Remaining Days', render: (value) => formatQty(value) },
            { key: 'seeded', label: 'Seeded', render: (value) => value ? 'Yes' : 'No' },
            { key: 'unseeded', label: 'Unseeded', render: (value) => value ? 'Yes' : 'No' },
          ]}
        />
        <DetailTable
          title="Corrections"
          rows={detailedRows.correction_rows || []}
          onDownload={() => handleDownloadSection('corrections')}
          columns={[
            { key: 'line_type', label: 'Type' },
            { key: 'amount', label: 'Amount', render: (value) => value != null ? formatCurrency(value) : '-' },
            { key: 'opening_qty', label: 'Opening Qty', render: (value) => value != null ? formatQty(value) : '-' },
            { key: 'opening_value', label: 'Opening Value', render: (value) => value != null ? formatCurrency(value) : '-' },
            { key: 'note', label: 'Note' },
            { key: 'created_at', label: 'Created At', render: (value) => formatDateTime(value) },
          ]}
        />
        <DetailTable
          title="Opening Journal Summary"
          rows={detailedRows.opening_journal_rows || []}
          onDownload={() => handleDownloadSection('opening-journal-summary')}
          columns={[
            { key: 'line_type', label: 'Type' },
            { key: 'customer_name', label: 'Customer' },
            { key: 'vendor_name', label: 'Vendor' },
            { key: 'account_code', label: 'Account' },
            { key: 'amount', label: 'Amount', render: (value) => formatCurrency(value) },
            { key: 'entry_date', label: 'Entry Date', render: (value) => formatDateTime(value) },
          ]}
        />
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">FY</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Snapshot</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Generated By</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {history.map((item) => (
                  <tr key={item.id}>
                    <td className="py-3 px-4 text-[#2D2420] font-medium">{item.fy_key}</td>
                    <td className="py-3 px-4"><SeverityBadge value={item.status === 'openings_generated' ? 'ok' : item.status === 'confirmed' ? 'warning' : item.summary?.blocking_issue_count ? 'blocking' : 'ok'} /></td>
                    <td className="py-3 px-4 text-[#777]">{formatDateTime(item.snapshot_at)}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{item.generated_by?.name || '-'}</td>
                    <td className="py-3 px-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            const res = await adminApi.getFinancialYearClosureById(item.id, { include_lines: true });
                            setClosure(res.data || null);
                            setSelectedFy(item.fy_key);
                          } catch (error) {
                            toast.error('Failed to load FY closure detail');
                          }
                        }}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
                {!history.length && !loading ? (
                  <tr><td colSpan={5} className="py-8 text-center text-[#A8A8A8]">No FY closure history yet</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
